/**
 * Session timeout warning and auto-logout logic.
 *
 * Monitors the user's session duration client-side and displays a countdown
 * modal before automatically signing out. Syncs across tabs via broadcast.
 */

import showFlash from './show_flash';

export default function initSessionInformer() {
  const $informer = $('#session-informer');
  if (!$informer.length) return;

  const sessionUrl = `${window.location.origin}/usr/session_persister`;
  const originalTitle = document.title;
  let warningTimerId = null;
  let logoutTimerId = null;

  const MAX_TIMEOUT = 2_147_483_647; // 2^31 - 1 (max safe setTimeout delay)

  const SESSION_DURATION = parseInt($informer.data('sessionDuration'), 10);
  const NOTIFY_AT = parseInt($informer.data('sessionNotifyAt'), 10);

  // Don't run if config is missing or invalid
  if (!SESSION_DURATION || !NOTIFY_AT || SESSION_DURATION <= NOTIFY_AT) return;

  function restoreTitle() {
    document.title = originalTitle;
  }

  function setWarningTitle() {
    document.title = `Logout Warning - ${originalTitle}`;
  }

  function showWarning() {
    setWarningTitle();

    // Start countdown and logout timer once the modal finishes opening so they stay in sync
    $informer.one('open.zf.reveal', () => {
      $('#session-informer-continue').focus();

      const $countdown = $informer.find('.notify_countdown');

      if (typeof window.notifyCountdown === 'function') {
        window.notifyCountdown($countdown);
      }

      const countdownSeconds = parseInt($countdown.text(), 10);
      logoutTimerId = setTimeout(() => {
        window.signOut();
      }, countdownSeconds * 1000);
    });

    $informer.foundation('open');
  }

  function closeWarnings() {
    $('#session-ended').foundation('close');
    $informer.foundation('close');
    if (typeof window.clearCountdownInterval === 'function') {
      window.clearCountdownInterval();
    }
    // Reset the displayed countdown value so it doesn't show a stale number on next open
    $informer.find('.notify_countdown').text(NOTIFY_AT / 1000);
  }

  function scheduleWarning() {
    const timeUntilWarning = SESSION_DURATION - NOTIFY_AT;
    warningTimerId = setTimeout(showWarning, Math.min(timeUntilWarning, MAX_TIMEOUT));
  }

  function resetTimers() {
    if (warningTimerId) clearTimeout(warningTimerId);
    if (logoutTimerId) clearTimeout(logoutTimerId);
    scheduleWarning();
  }

  $('#session-informer-continue').click(() => {
    // Cancel the logout timer immediately so it can't fire while waiting for the response
    if (logoutTimerId) clearTimeout(logoutTimerId);
    logoutTimerId = null;

    $.post(sessionUrl)
      .done((data) => {
        if (data.continued) {
          window.onSessionContinued();
          window.postSessionBroadcast('sessionContinued');
        } else {
          window.signOut();
        }
      })
      .fail(() => {
        closeWarnings();
        showFlash(
          'warning',
          'Unable to extend your login session. Please save your work and refresh the page.',
        );
      });
  });

  // Treat closing any modal (overlay click, Escape, close button) as continuing the session.
  // The actual event fired is 'closed.zf.reveal', namespaced so .off() can remove it.
  $(window).on('closed.zf.reveal.sessionInformer', () => {
    if (logoutTimerId) {
      clearTimeout(logoutTimerId);
      logoutTimerId = null;
      $('#session-informer-continue').trigger('click');
    }
  });

  $('#session-informer-end').click(() => {
    // Detach the closed handler so it doesn't try to continue the session during sign-out
    $(window).off('closed.zf.reveal.sessionInformer');
    restoreTitle();
    window.signOut();
  });

  window.openSessionEnded = () => {
    if (warningTimerId) clearTimeout(warningTimerId);
    if (logoutTimerId) clearTimeout(logoutTimerId);
    warningTimerId = null;
    logoutTimerId = null;
    // Detach so the modal close doesn't trigger a "continue" attempt
    $(window).off('closed.zf.reveal.sessionInformer');
    restoreTitle();
    $informer.foundation('close');
    $('#session-ended').foundation('open');
  };

  window.onSessionStarted = () => {
    closeWarnings();
    resetTimers();
    restoreTitle();
  };

  window.onSessionContinued = () => {
    window.onSessionStarted();
  };

  // Sync other open tabs on page load
  window.postSessionBroadcast('sessionStarted');

  // Start the timer
  scheduleWarning();
}
